import { getRrwebIframe } from '@carboncopyco/mimesis-shared';
import {
  FrameType,
  type Tour,
  type TourFrame,
  type TourStep,
} from '@carboncopyco/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type MutableRefObject, useEffect, useMemo, useState } from 'react';
import { setActiveFrameId, setActiveStepId } from '../../store';
import type { FramesWarnings } from '../../types/frames';
import type { frameSeparation } from '../../utils/eventIndex';
import { getStepWarningActive } from '../../utils/getPositionWarning';
import { initGridResizer } from '../../utils/gridResizer';
import { scrollToTargetElement } from '../../utils/replayUtils';
import { getFrameIndetifier, getParentFrame } from '../../utils/replayer';
import { useTourDispatch } from '../store';
import { useIncrementalNavigation } from './useIncrementalNavigation';

interface IUseReplayerActions {
  isReady: boolean;
  playerRef: MutableRefObject<any>;
  frameChunks: frameSeparation[];
  playableFrames: TourFrame[];
  playableSteps: TourStep[];
  activeFrame: TourFrame;
  activeStep: TourStep;
  uuid: string;
  settings: Tour['tourSettings'] | Tour['draftSettings'];
  rootElement: MutableRefObject<HTMLElement>;
  addingStepInProgress?: boolean;
  onStepChange?: (stepData: {
    stepId: TourStep['stepId'];
    eventType: TourStep['eventType'];
    stepIndex: number;
    frameIndex: TourFrame['frameIndex'];
    completed: boolean;
  }) => void;
  isPreview?: boolean;
  framesWarnings?: FramesWarnings;
}

export const useReplayerActions = ({
  isReady,
  playerRef,
  frameChunks,
  playableFrames,
  playableSteps,
  activeFrame,
  activeStep,
  uuid,
  settings,
  rootElement,
  addingStepInProgress = false,
  onStepChange,
  isPreview = false,
  framesWarnings,
}: IUseReplayerActions) => {
  const flags = useFlags();
  const dispatch = useTourDispatch();

  const [lastPlayedStep, setLastPlayedStep] = useState<TourStep | null>(null);
  const [lastPlayedFrameId, setLastPlayedFrameId] = useState<number | null>(
    null
  );

  const [isInitialFramePlayed, setIsInitialFramePlayed] =
    useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const nonHiddenPlayableFrames = useMemo(() => {
    return playableFrames.filter((frame) => frame.isActive);
  }, [playableFrames]);
  const incrementalStepNavigation = useIncrementalNavigation({
    playableFrames,
    playableSteps,
    activeFrame,
    activeStep,
    playFrame,
    uuid,
  });

  function playFrame(frame: TourFrame) {
    const chunkToPlay =
      frame.frameType === 'screenshot'
        ? { screenshotUrl: frame.frameScreenshotUrl }
        : frameChunks.find(
            (frameChunk) =>
              frameChunk.frameReference === getFrameIndetifier(frame)
          );
    if (!chunkToPlay) {
      return;
    }

    playerRef.current?.playTourAtTime(
      chunkToPlay.playerTime,
      chunkToPlay.event,
      chunkToPlay.screenshotUrl
    );
    dispatch(setActiveFrameId({ uuid, frameId: frame.id }));
    dispatch(
      setActiveStepId({
        uuid,
        stepId: frame.steps[0]?.stepId ?? null,
      })
    );
    setLastPlayedFrameId(frame.id);
  }

  const playFrameByStepId = (stepId: string) => {
    if (playableFrames?.length === 0) {
      return;
    }

    const parentFrame = getParentFrame(playableFrames, {
      stepId,
    } as TourStep);
    if (!parentFrame) {
      return;
    }

    if (parentFrame.id === activeFrame?.id) {
      return;
    }

    playFrame(parentFrame);
  };

  const playStep = (stepId: TourStep['stepId']) => {
    playFrameByStepId(stepId);
    dispatch(setActiveStepId({ uuid, stepId }));

    refreshTooltip(playableSteps.find((step) => step.stepId === stepId));
  };

  const showTooltip = ({
    step,
    isEditable,
    frameId,
    isPositionWarningActive,
  }: {
    step: TourStep;
    isEditable: boolean;
    frameId: TourFrame['id'];
    isPositionWarningActive?: boolean;
  }) => {
    if (!step || !playerRef.current) {
      return;
    }
    const stepPosition = playableSteps.findIndex(
      (playableStep) => playableStep.stepId === step.stepId
    );

    const lastNonHiddenPlayableFrameWithSteps = [...nonHiddenPlayableFrames]
      .reverse()
      .find((frame) => frame.steps.length);

    const lastStep =
      lastNonHiddenPlayableFrameWithSteps.steps[
        lastNonHiddenPlayableFrameWithSteps.steps.length - 1
      ];

    const frameType = playableFrames?.find(
      (frame) => frame.id === frameId
    )?.frameType;

    playerRef.current.showStep({
      isEditor: !isPreview,
      tourSettings: settings,
      frames: playableFrames,
      step: step,
      isStepEditable: isEditable,
      isTourFirstStep: stepPosition === 0,
      isTourLastStep: step.stepId === lastStep.stepId,
      isPositionWarningActive,
      frameType: frameType,
    });
    const stepData = {
      uuid,
      stepId: step.stepId,
      stepIndex: stepPosition,
      frameIndex: playableFrames.findIndex(
        (frame) => frame.id === activeFrame.id
      ),
      completed: stepPosition === playableSteps.length - 1,
      eventType: step.eventType,
    };
    postMessage?.({
      type: 'onStepChange',
      payload: stepData,
    });
    onStepChange?.(stepData);

    setLastPlayedStep(step);
  };

  const onPlayHandler = () => {
    setIsPaused(false);
  };

  const onPauseHandler = () => {
    setIsPaused(true);
  };

  const refreshTooltip = (step?: TourStep) => {
    if ((!activeStep && !step) || !activeFrame) {
      return;
    }
    showTooltip({
      step: step || activeStep,
      isEditable: activeFrame.isActive,
      frameId: activeFrame.id,
      isPositionWarningActive: getStepWarningActive(
        framesWarnings,
        activeFrame.id,
        activeStep.stepId,
        'position'
      ),
    });
  };

  const onTooltipDisplayed = () => {
    if (!activeStep) {
      return;
    }

    // since in desktop with multiple tabs open we can have multiple iframes in document,
    // we want to search for iframe only in current tab (rootElement) instead of whole document
    const iframeDocument = getRrwebIframe(rootElement?.current)?.contentWindow
      ?.document;

    if (iframeDocument && flags.tourGainsightGridResize) {
      initGridResizer(iframeDocument);
    }

    if (!addingStepInProgress) {
      scrollToTargetElement({
        step: activeStep,
        iframeDocument: iframeDocument,
        frameType:
          playableFrames?.find((frame) => frame.id === activeFrame.id)
            ?.frameType || FrameType.recording,
      });
    }
  };

  useEffect(() => {
    // Sets first active frame and first active step
    if (!isReady || playableFrames?.length === 0 || isInitialFramePlayed) {
      return;
    }

    const initialFrameReplayTimeout = setTimeout(() => {
      playFrame(playableFrames[0]);
      setIsInitialFramePlayed(true);
    }, 1000);

    return () => clearInterval(initialFrameReplayTimeout);
  }, [isReady, playableFrames, isInitialFramePlayed]);

  useEffect(() => {
    /*
      Actually plays the frame/step on every action (click of frame in
      editor | next or back in preview)
    */

    if (!activeStep || !activeFrame) {
      return;
    }

    if (!isPaused) {
      return;
    }

    const parentFrame = getParentFrame(playableFrames, activeStep);
    if (!parentFrame) {
      return;
    }

    if (parentFrame.id !== activeFrame.id) {
      playFrame(parentFrame);
      return;
    }

    if (
      lastPlayedStep?.stepId === activeStep?.stepId &&
      lastPlayedFrameId === activeFrame?.id
    ) {
      // No need to re-show tooltip while editing
      return;
    }
    showTooltip({
      step: activeStep,
      isEditable: parentFrame.isActive,
      frameId: parentFrame.id,
      isPositionWarningActive: getStepWarningActive(
        framesWarnings,
        parentFrame.id,
        activeStep.stepId,
        'position'
      ),
    });
  }, [
    playableFrames,
    activeFrame,
    activeStep,
    isPaused,
    lastPlayedStep,
    incrementalStepNavigation,
  ]);

  return {
    lastPlayedStep,
    isPaused,
    playFrame,
    playFrameByStepId,
    playStep,
    showTooltip,
    incrementalStepNavigation,
    onPlayHandler,
    onPauseHandler,
    refreshTooltip,
    onTooltipDisplayed,
  };
};
